import { Suspense, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { motion, useInView } from "framer-motion";
import { Layers, Magnet, Move3d, Radio, Shield, Square } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import TagModel from "@/components/three/TagModel";
import { usePlatform } from "@/context/PlatformContext";
import hasWebGL from "@/lib/hasWebGL";
import { IMAGES } from "@/data/products";

const buildParts = (config) => ([
  {
    id: "tag",
    num: "01",
    icon: Radio,
    name: "Smart Tracker Tag",
    desc: `${config.network}-compatible Bluetooth module, 3.17 cm across and 8 mm thin, with a built-in speaker. ${config.requirements}.`,
  },
  {
    id: "holder",
    num: "02",
    icon: Shield,
    name: "Magnetic Protective Case",
    desc: "Matte black ABS case (4.6 × 3.5 cm) that holds the tag and attaches straight to steel — no bracket required.",
  },
  {
    id: "magnet",
    num: "03",
    icon: Magnet,
    name: "Built-in Magnet",
    desc: "Seated in the base of the case — attaches to vehicle chassis, trailers, tools and steel boxes."
  },
  {
    id: "sheet",
    num: "04",
    icon: Square,
    name: "Metal Sheet ×2",
    desc: "Two adhesive steel plates so the magnet holds on wood, plastic, fabric — anything non-metal.",
  },
]);

const FINISH_OPTIONS = [
  { id: "obsidian", label: "Obsidian Black", hex: "#1D1D1F" },
];

const Anatomy = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { margin: "200px 0px" });
  const [finish, setFinish] = useState("obsidian");
  const [exploded, setExploded] = useState(true);
  const [highlight, setHighlight] = useState(null);
  const explode = useRef(1);
  const spin = useRef({ vel: 0, dragging: false, lastX: 0 });
  const { config: platformCfg, platform } = usePlatform();
  const PARTS = buildParts(platformCfg);
  // Facebook / Instagram Android WebView and some restricted iOS Safari
  // builds don't support WebGL — Canvas creation throws
  // "THREE.WebGLRenderer: Error creating WebGL context." and leaves a blank
  // panel. Feature-detect once and render a clean product-image fallback
  // for those shoppers instead of a broken 3D region.
  const webglOK = hasWebGL();
  const fallbackImg = platform === "android" ? (IMAGES.pack1Android || IMAGES.pack1) : IMAGES.pack1;

  const toggleExplode = () => {
    const next = !exploded;
    setExploded(next);
    explode.current = next ? 1 : 0;
  };

  const onPointerDown = (e) => {
    spin.current.dragging = true;
    spin.current.lastX = e.clientX;
  };
  const onPointerMove = (e) => {
    if (!spin.current.dragging) return;
    spin.current.vel = (e.clientX - spin.current.lastX) * 0.004;
    spin.current.lastX = e.clientX;
  };
  const endDrag = () => {
    spin.current.dragging = false;
  };

  return (
    <section
      id="anatomy"
      ref={sectionRef}
      data-testid="anatomy-section"
      className="scroll-mt-24 px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-5">
          <Reveal>
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-tracksy-orange">
              Inside every pack
            </p>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-tracksy-ink sm:text-4xl">
              Four parts. One system that sticks.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-tracksy-sub">
              Every Tracksy tag ships as a complete kit — tag, magnetic case and
              mounting sheets. Hover a part to see it light up in the model. Two metal sheets are included for non-metal surfaces. Runs on a replaceable CR2025 coin cell.
            </p>
          </Reveal>

          <ul className="mt-8 space-y-2" data-testid="anatomy-parts-list">
            {PARTS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <li
                  data-testid={`anatomy-part-${p.id}`}
                  onMouseEnter={() => setHighlight(p.id)}
                  onMouseLeave={() => setHighlight(null)}
                  onFocus={() => setHighlight(p.id)}
                  onBlur={() => setHighlight(null)}
                  tabIndex={0}
                  className={`group flex cursor-default gap-4 rounded-2xl border p-4 transition-all duration-300 ${
                    highlight === p.id
                      ? "border-tracksy-orange/40 bg-white shadow-lift"
                      : "border-transparent hover:border-black/5 hover:bg-white"
                  }`}
                >
                  <span className="font-heading text-xs font-extrabold text-tracksy-orange">{p.num}</span>
                  <div>
                    <p className="flex items-center gap-2 font-heading text-base font-bold text-tracksy-ink">
                      <p.icon className="h-4 w-4 text-tracksy-orange" />
                      {p.name}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-tracksy-sub">{p.desc}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>

        <div className="order-1 lg:order-2 lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-[2.5rem] border border-black/5 bg-tracksy-cream shadow-subtle">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,107,0,0.08),_transparent_60%)]" />
              <div
                data-testid="anatomy-3d-canvas"
                data-webgl={webglOK ? "on" : "off"}
                className="relative aspect-[4/5] w-full cursor-grab touch-pan-y select-none active:cursor-grabbing sm:aspect-square"
                onPointerDown={webglOK ? onPointerDown : undefined}
                onPointerMove={webglOK ? onPointerMove : undefined}
                onPointerUp={webglOK ? endDrag : undefined}
                onPointerLeave={webglOK ? endDrag : undefined}
                onPointerCancel={webglOK ? endDrag : undefined}
              >
                {webglOK ? (
                  <Canvas
                    shadows
                    dpr={[1, 1.75]}
                    frameloop={inView ? "always" : "never"}
                    camera={{ position: [0, 1.2, 15], fov: 30 }}
                    gl={{ antialias: true, alpha: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.05 }}
                  >
                    <Suspense fallback={null}>
                      <TagModel finish={finish} explode={explode} highlight={highlight} spin={spin} />
                    </Suspense>
                  </Canvas>
                ) : (
                  <img
                    src={fallbackImg}
                    alt="Tracksy magnetic tracker — tag, protective case and mounting sheets"
                    className="h-full w-full select-none object-contain p-6"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>

              {webglOK && (
                <div className="pointer-events-none absolute left-5 top-5 flex items-center gap-2 rounded-full border border-black/5 bg-white/80 px-3.5 py-2 text-xs font-semibold text-tracksy-sub backdrop-blur-xl">
                  <Move3d className="h-3.5 w-3.5 text-tracksy-orange" />
                  Drag to rotate · Auto-spins
                </div>
              )}

              {webglOK && (
                <div className="absolute inset-x-5 bottom-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 rounded-full border border-black/5 bg-white/85 p-1.5 backdrop-blur-xl">
                  {FINISH_OPTIONS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      data-testid={`anatomy-finish-${f.id}`}
                      onClick={() => setFinish(f.id)}
                      aria-label={f.label}
                      title={f.label}
                      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                        finish === f.id ? "bg-tracksy-ink text-white" : "text-tracksy-sub hover:text-tracksy-ink"
                      }`}
                    >
                      <span
                        className="h-3.5 w-3.5 rounded-full border border-black/15"
                        style={{ backgroundColor: f.hex }}
                      />
                      {f.label.split(" ")[1]}
                    </button>
                  ))}
                </div>
                <motion.button
                  type="button"
                  data-testid="anatomy-explode-toggle"
                  onClick={toggleExplode}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 rounded-full bg-tracksy-orange px-4 py-2.5 text-xs font-bold text-white shadow-glow transition-colors hover:bg-tracksy-orangeHover"
                >
                  <Layers className="h-3.5 w-3.5" />
                  {exploded ? "Assemble" : "Explode view"}
                </motion.button>
              </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Anatomy;
